import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useContratos, useContrato } from '@/hooks/useContratos'
import { formatCurrency, getStatusBadgeClass, formatDate } from '@/lib/formatters'
import { calculateContratoStatus, translateCalculatedStatus } from '@/lib/status-logic'
import { useFaturas } from '@/hooks/useFaturas'
import { 
    Car, 
    ShieldCheck, 
    ArrowLeft, 
    Download, 
    Calendar,
    FileText,
    ExternalLink,
    AlertCircle,
    CheckCircle2,
    Copy,
    Check,
    Receipt,
    Clock,
    ChevronDown,
    Hash,
    Info,
    RefreshCw
} from 'lucide-react'
import { usePortalStore } from '@/stores/portal.store'
import { supabase } from '@/config/supabase'
import { jsPDF } from 'jspdf'

// ── Hook: busca cobranças diretamente do Asaas via Edge Function ──────────────
function useAsaasCharges(contratoId: string | null | undefined, externalReference: string | null | undefined, cpf: string | null | undefined) {
    return useQuery({
        queryKey: ['asaas-charges', contratoId, externalReference],
        queryFn: async () => {
            if (!(contratoId || externalReference) || !cpf) return []
            const { data, error } = await supabase.functions.invoke('get-contract-charges', {
                body: { contrato_id: contratoId, external_reference: externalReference, cpf }
            })
            if (error) throw error
            return data?.data || []
        },
        enabled: (!!contratoId || !!externalReference) && !!cpf,
        staleTime: 30_000, // 30 segundos de cache
    })
}

export function PortalContratoDetailPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const associado = usePortalStore(s => s.associado)
    const portalCpf = usePortalStore(s => s.cpf)
    const PORTAL_ASSOCIADO_ID = associado?.id || ''

    const { data: result, isLoading: loadingContratos } = useContratos({ associado_id: PORTAL_ASSOCIADO_ID, pageSize: 50 })
    const { data: faturasResult } = useFaturas({ cpf: portalCpf || '', pageSize: 50 })

    const { data: contratoDetail, isLoading: loadingDetail } = useContrato(id || null)

    const contratoList = result?.data?.find(c => c.id === id)
    const contrato = contratoDetail || contratoList

    // Busca cobranças DIRETO do Asaas usando o ID do contrato (legado) ou o external_reference novo
    const { 
        data: asaasCharges, 
        isLoading: loadingCharges,
        refetch: refetchCharges,
        isFetching
    } = useAsaasCharges(contrato?.id, contrato?.external_reference, portalCpf)

    const isLoading = loadingContratos || loadingDetail || (!!(contrato?.id || contrato?.external_reference) && loadingCharges)

    // Status calculado a partir do Asaas (prioritário) ou locais
    let realStatus = contrato?.status as any;
    if (realStatus !== 'cancelado' && realStatus !== 'suspenso') {
        if (asaasCharges && asaasCharges.length > 0) {
            const isOverdue = asaasCharges.some((c: any) => c.status === 'OVERDUE')
            const isAdesaoPendente = asaasCharges.some((c: any) => c.status === 'PENDING' && (c.description || '').toLowerCase().includes('ades'))
            
            if (isOverdue) realStatus = 'inadimplente'
            else if (isAdesaoPendente) realStatus = 'pendente_adesao'
            else realStatus = 'ativo'
        } else {
            const allLocalFaturas = faturasResult?.data || []
            realStatus = contrato ? calculateContratoStatus(contrato, allLocalFaturas) : 'ativo'
        }
    }
    const metadados: any = contrato?.metadata || {}


    const [isGenerating, setIsGenerating] = useState(false)

    const handleDownloadPDF = async () => {
        if (!contrato || isGenerating) return
        setIsGenerating(true)
        try {
            const doc = new jsPDF()
            const metadados: any = contrato.metadata || {}
            const sig = metadados.assinatura_digital
        
        // Helper para formatar data em Horário de Brasília
        const formatBR = (dateStr: string) => {
            try {
                return new Date(dateStr).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })
            } catch (e) {
                return dateStr
            }
        }

        // ─── PÁGINA 1: QUADRO RESUMO E IDENTIFICAÇÃO ─────────────────────
        doc.setFillColor(14, 165, 233) // Primary color (Sky 500 equivalent)
        doc.rect(0, 0, 210, 40, 'F')
        
        doc.setTextColor(255, 255, 255)
        doc.setFontSize(22)
        doc.setFont("helvetica", "bold")
        doc.text("CONTRATO DE PRESTAÇÃO DE SERVIÇOS", 105, 25, { align: 'center' })
        
        doc.setTextColor(30, 41, 59)
        doc.setFontSize(10)
        doc.setFont("helvetica", "normal")
        doc.text(`Identificador Único: ${contrato.id}`, 20, 50)
        doc.text(`Referência: ${contrato.external_reference || 'N/A'}`, 20, 56)
        doc.text(`Emitido em: ${formatBR(new Date().toISOString())}`, 20, 62)

        // 1. Partes Contratantes
        doc.setFontSize(14)
        doc.setFont("helvetica", "bold")
        doc.text("1. DA PARTE CONTRATANTE", 20, 80)
        doc.line(20, 83, 190, 83)
        
        doc.setFontSize(11)
        doc.setFont("helvetica", "normal")
        doc.text(`CONTRATANTE: ${associado?.nome || 'N/A'}`, 20, 93)
        doc.text(`CPF: ${associado?.cpf || 'N/A'}`, 20, 100)
        doc.text(`E-mail: ${associado?.email || 'N/A'}`, 20, 107)

        // 2. Do Objeto e Valores
        doc.setFontSize(14)
        doc.setFont("helvetica", "bold")
        doc.text("2. DO OBJETO E CONDIÇÕES COMERCIAIS", 20, 125)
        doc.line(20, 128, 190, 128)
        
        doc.setFontSize(11)
        doc.setFont("helvetica", "normal")
        doc.text(`Serviço/Produto: ${metadados.servico_nome || 'Prestação de Serviços'}`, 20, 138)
        doc.text(`Valor Mensal: ${formatCurrency(contrato.valor_total_mensal)}`, 20, 145)
        doc.text(`Vencimento: Todo dia ${contrato.dia_vencimento}`, 20, 152)
        doc.text(`Data de Início: ${formatDate(contrato.data_inicio)}`, 20, 159)
        
        const temVeiculo = ((contrato as any).veiculo_placa && (contrato as any).veiculo_placa !== 'N/A') || metadados.placa
        if (temVeiculo) {
            const displayPlaca = (contrato as any).veiculo_placa || metadados.placa
            const displayModelo = (contrato as any).veiculo_modelo || 'Veículo Protegido'
            doc.text(`Veículo Vinculado: ${displayModelo} (Placa: ${displayPlaca})`, 20, 166)
        }

        // Rodapé Página 1
        doc.setFontSize(8)
        doc.setTextColor(150, 150, 150)
        doc.text("Este documento possui validade jurídica mediante assinatura digital vinculada ao manifesto de auditoria anexo.", 105, 285, { align: 'center' })

        // ─── PÁGINA 2: TEXTO DOS TERMOS ─────────────────────
        if (sig && sig.documento_base) {
            doc.addPage()
            doc.setTextColor(30, 41, 59)
            doc.setFontSize(14)
            doc.setFont("helvetica", "bold")
            doc.text("3. DOS TERMOS E CONDIÇÕES ACEITOS", 20, 20)
            doc.line(20, 23, 190, 23)
            
            doc.setFontSize(9)
            doc.setFont("helvetica", "normal")
            const termsText = sig.documento_base
            const splitTerms = doc.splitTextToSize(termsText, 170)
            
            let yPos = 33
            splitTerms.forEach((line: string) => {
                if (yPos > 275) {
                    doc.addPage()
                    yPos = 20
                }
                doc.text(line, 20, yPos)
                yPos += 4.5
            })
        }

        // ─── PÁGINA FINAL: MANIFESTO DE ASSINATURA (AUDIT TRAIL) ─────────────────────
        if (sig) {
            doc.addPage()
            
            // Cabeçalho Audit Trail
            doc.setFillColor(248, 250, 252)
            doc.rect(0, 0, 210, 45, 'F')
            doc.setTextColor(15, 23, 42)
            doc.setFontSize(18)
            doc.setFont("helvetica", "bold")
            doc.text("MANIFESTO DE ASSINATURA DIGITAL", 20, 28)
            
            doc.setFontSize(9)
            doc.setFont("helvetica", "normal")
            doc.setTextColor(100, 116, 139)
            doc.text("AUDIT TRAIL - EVIDÊNCIAS DE CONFORMIDADE LEGAL (MP 2.200-2/2001)", 20, 38)

            // Evidências
            doc.setTextColor(30, 41, 59)
            doc.setFontSize(12)
            doc.setFont("helvetica", "bold")
            doc.text("EVIDÊNCIAS DA TRANSAÇÃO", 20, 60)
            doc.line(20, 63, 190, 63)
            
            doc.setFontSize(10)
            doc.setFont("helvetica", "normal")
            doc.text(`Data/Hora do Aceite: ${formatBR(sig.timestamp)} (Horário de Brasília)`, 20, 75)
            doc.text(`Endereço IP: ${sig.ip_real || sig.ip_estimado || 'N/A'}`, 20, 82)
            doc.text(`Assinado por: ${associado?.nome || 'N/A'} (CPF: ${associado?.cpf || 'N/A'})`, 20, 89)
            
            const ua = sig.user_agent || 'N/A'
            const splitUA = doc.splitTextToSize(`Dispositivo/Browser: ${ua}`, 170)
            doc.text(splitUA, 20, 96)

            // Hash de Integridade
            const nextY = 96 + (splitUA.length * 5) + 10
            doc.setFillColor(241, 245, 249)
            doc.setDrawColor(203, 213, 225)
            doc.roundedRect(20, nextY, 120, 30, 2, 2, 'FD') // Diminuí para caber o QR Code ao lado
            
            doc.setFontSize(9)
            doc.setFont("helvetica", "bold")
            doc.text("HASH DE INTEGRIDADE SHA-256", 25, nextY + 10)
            doc.setFont("courier", "normal")
            doc.text(sig.hash || 'N/A', 25, nextY + 20, { maxWidth: 110 })

            // QR Code de Validação (QuickChart via Image + Canvas - suporte nativo a CORS)
            const validationUrl = `https://all.acsm.org.br/validar/${contrato.id}`
            const qrUrl = `https://quickchart.io/qr?text=${encodeURIComponent(validationUrl)}&size=150`
            
            try {
                const qrBase64 = await new Promise<string>((resolve, reject) => {
                    const img = new Image();
                    img.crossOrigin = 'anonymous';
                    img.onload = () => {
                        const canvas = document.createElement('canvas');
                        canvas.width = img.width;
                        canvas.height = img.height;
                        const ctx = canvas.getContext('2d');
                        ctx?.drawImage(img, 0, 0);
                        resolve(canvas.toDataURL('image/png'));
                    };
                    img.onerror = () => reject(new Error('Img Load Error'));
                    img.src = qrUrl;
                    // Timeout de 5s
                    setTimeout(() => reject(new Error('Timeout')), 5000);
                });
                doc.addImage(qrBase64, 'PNG', 155, nextY - 5, 30, 30);
            } catch (err) {
                console.warn('Falha no QR Code:', err);
                doc.setFontSize(7);
                doc.text("Valide em:", 155, nextY + 10);
                doc.text("all.acsm.org.br/validar", 155, nextY + 15);
            }

            // Texto de Validabilidade (Estilo ITI)
            doc.setFont("helvetica", "normal")
            doc.setFontSize(8)
            doc.setTextColor(71, 85, 105)
            const itistyleText = `Documento assinado eletronicamente de acordo com a MP 2.200-2/2001. A autenticidade e a integridade deste documento podem ser validadas através do QR Code ao lado ou acessando: ${validationUrl}`
            const splitITI = doc.splitTextToSize(itistyleText, 170)
            doc.text(splitITI, 20, nextY + 45)
        }

        doc.save(`Contrato_${contrato.external_reference || contrato.id}.pdf`)
    } catch (err) {
        console.error('Erro ao gerar PDF:', err)
        alert('Erro ao gerar o contrato. Por favor, tente novamente.')
    } finally {
        setIsGenerating(false)
    }
}

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[70vh]">
                <div className="w-10 h-10 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mb-4" />
                <p className="text-surface-700/60 font-medium animate-pulse">Carregando Central do Contrato...</p>
            </div>
        )
    }

    if (!contrato) {
        return (
            <div className="p-8 text-center max-w-md mx-auto min-h-[60vh] flex flex-col justify-center">
                <div className="w-20 h-20 bg-warning-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <AlertCircle size={40} className="text-warning-500" />
                </div>
                <h1 className="text-2xl font-bold text-surface-900 mb-2">Contrato não encontrado</h1>
                <p className="text-surface-700/60 mb-8">Não conseguimos localizar as informações desta proteção.</p>
                <button onClick={() => navigate('/portal/contratos')}
                    className="w-full py-4 bg-primary-600 text-white rounded-2xl font-bold">
                    Voltar para Meus Contratos
                </button>
            </div>
        )
    }

    const veiculo_placa = (contrato as any).veiculo_placa
    const veiculo_modelo = (contrato as any).veiculo_modelo

    return (
        <div className="p-4 space-y-6 animate-fade-in max-w-2xl mx-auto pb-24">

            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/portal/contratos')}
                        className="w-12 h-12 rounded-2xl bg-white border border-surface-100 flex items-center justify-center shadow-sm active:scale-90 transition-transform">
                        <ArrowLeft size={24} className="text-surface-700" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-black text-surface-900 leading-none">Central do Contrato</h1>
                        <div className="flex items-center gap-1.5 mt-1.5">
                            <Hash size={12} className="text-primary-600" />
                            <span className="text-sm font-bold text-primary-600">{contrato.external_reference || 'S/ REF'}</span>
                        </div>
                    </div>
                </div>
                <div className={`px-4 py-2 rounded-2xl font-black text-xs uppercase tracking-widest ${getStatusBadgeClass(realStatus)}`}>
                    {translateCalculatedStatus(realStatus)}
                </div>
            </div>

            {/* Main Card */}
            <div className="rounded-[2.5rem] bg-white border border-surface-100 shadow-xl overflow-hidden">
                <div className="p-8 bg-gradient-to-br from-surface-50 to-white">
                    <div className={`w-20 h-20 rounded-[2rem] flex items-center justify-center mb-8
                        ${realStatus === 'ativo' ? 'bg-success-100/50' : 'bg-warning-100/50'}`}>
                        {veiculo_placa 
                            ? <Car size={40} className={realStatus === 'ativo' ? 'text-success-600' : 'text-warning-600'} />
                            : <ShieldCheck size={40} className="text-blue-600" />
                        }
                    </div>
                    <h2 className="text-3xl font-black text-surface-900 leading-none mb-1">
                        {veiculo_placa || (metadados.placa || (metadados as any).servico_nome || 'Proteção Pessoal')}
                    </h2>
                    <p className="text-lg text-surface-700/60 font-semibold mb-8">
                        {veiculo_modelo || (metadados.placa ? 'Veículo Vinculado' : 'Associado Protegido')}
                    </p>
                    <div className="grid grid-cols-2 gap-6 pt-8 border-t border-surface-100/50">
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.2em] font-black text-surface-700/40">Valor Mensal</p>
                            <p className="text-2xl font-black text-surface-900">{formatCurrency(contrato.valor_total_mensal)}</p>
                        </div>
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.2em] font-black text-surface-700/40">Vencimento</p>
                            <p className="text-2xl font-black text-surface-900">Todo dia {contrato.dia_vencimento}</p>
                        </div>
                    </div>
                </div>
                <div className="p-4 bg-surface-50/50">
                    <button onClick={handleDownloadPDF}
                        disabled={isGenerating}
                        className="w-full h-14 bg-surface-900 text-white rounded-2xl font-black flex items-center justify-center gap-3 active:scale-[0.98] transition-all disabled:opacity-50">
                        {isGenerating ? <RefreshCw size={20} className="animate-spin" /> : <Download size={20} />}
                        {isGenerating ? 'GERANDO PDF...' : 'BAIXAR CONTRATO PDF'}
                    </button>
                </div>
            </div>

            {/* Dados Técnicos */}
            <div className="space-y-3">
                <div className="bg-white p-5 rounded-3xl border border-surface-100 flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <Calendar size={20} className="text-primary-600" />
                    </div>
                    <div>
                        <p className="text-[10px] uppercase font-black text-surface-700/40 tracking-wider">Início da Cobertura</p>
                        <p className="text-sm font-bold text-surface-900">
                            {realStatus === 'pendente_adesao' ? 'Após pagamento da adesão' : formatDate(contrato.data_inicio)}
                        </p>
                    </div>
                </div>
                {contrato.external_reference && (
                    <div className="bg-white p-5 rounded-3xl border border-surface-100 flex items-center gap-4">
                        <div className="w-12 h-12 bg-surface-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                            <Hash size={20} className="text-surface-400" />
                        </div>
                        <div>
                            <p className="text-[10px] uppercase font-black text-surface-700/40 tracking-wider">Chave do Contrato</p>
                            <p className="text-sm font-bold text-surface-900">{contrato.external_reference}</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Registro de Assinatura Digital */}
            {metadados.assinatura_digital && (
                 <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-[2rem] border border-primary-100 shadow-sm space-y-4">
                     <div className="flex items-center gap-3">
                         <ShieldCheck size={20} className="text-primary-600" />
                         <p className="text-xs uppercase font-black text-primary-600 tracking-[0.1em]">Assinatura Digital Ativa</p>
                     </div>
                     <div className="space-y-3">
                         <div>
                             <p className="text-[10px] uppercase font-black text-surface-400 tracking-wider">Hash SHA-256</p>
                             <p className="text-[10px] font-mono break-all text-surface-900 bg-white/50 p-2 rounded-lg border border-primary-50">
                                 {metadados.assinatura_digital.hash}
                             </p>
                         </div>
                         <div className="flex items-center justify-between py-2 border-t border-primary-100/50">
                             <span className="text-[10px] text-surface-500 font-bold uppercase">Data do Aceite</span>
                             <span className="text-xs font-bold text-surface-900">
                                {new Date(metadados.assinatura_digital.timestamp).toLocaleString('pt-BR')}
                             </span>
                         </div>
                     </div>
                 </div>
            )}

            {/* Descrição do Plano / Benefícios */}
            <div className="space-y-4">
                <div className="flex items-center gap-2 px-2">
                    <ShieldCheck size={20} className="text-success-600" />
                    <h3 className="text-base font-black text-surface-900 uppercase tracking-widest">
                        {(contrato.veiculo_id || metadados.placa) ? 'Benefícios Ativos' : 'Serviços Contratados'}
                    </h3>
                </div>
                <div className="bg-white p-6 border border-surface-100 rounded-3xl space-y-4">
                    {contrato.veiculo_id && (
                        <div className="grid gap-3 mb-6">
                            <BenefitItem text="Assistência 24h em todo Brasil" />
                            <BenefitItem text="Cobertura para Terceiros" />
                            <BenefitItem text="Proteção contra Furto e Roubo" />
                            <BenefitItem text="Guincho até 500km" />
                        </div>
                    )}
                    
                    {/* Serviços Dinâmicos (Cross-sell/Adicionais/Base) */}
                    {(contrato as any).servicos && (contrato as any).servicos.length > 0 ? (
                        <div className="space-y-3">
                            {((contrato as any).servicos || []).map((link: any) => (
                                <div key={link.id} className="flex items-center justify-between p-3 rounded-2xl bg-surface-50 border border-surface-100">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600">
                                            <ShieldCheck size={14} />
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-surface-900 leading-tight">
                                                {link.servico?.nome || metadados.servico_nome || 'Serviço'}
                                            </p>
                                            <p className="text-[10px] text-surface-500 font-medium">
                                                {link.ativo ? 'Ativo' : 'Inativo'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-sm text-surface-700 font-medium leading-relaxed">
                            {metadados.servico_description || metadados.servico_nome || 'Serviço de proteção e assistência contratado conforme os termos aceitos.'}
                        </div>
                    )}
                </div>
            </div>

            {/* Painel Financeiro — dados 100% do Asaas */}
            <div className="space-y-4 pt-4">
                <div className="flex justify-between items-center px-2">
                    <div className="flex items-center gap-2">
                        <Receipt size={20} className="text-primary-600" />
                        <h3 className="text-base font-black text-surface-900 uppercase tracking-widest">Painel Financeiro</h3>
                    </div>
                    <button
                        onClick={() => refetchCharges()}
                        disabled={isFetching}
                        className={`flex items-center gap-1.5 text-[10px] bg-surface-100 text-surface-600 px-3 py-1.5 rounded-full font-black uppercase tracking-tighter transition-all ${isFetching ? 'opacity-50 cursor-not-allowed' : 'hover:bg-surface-200'}`}
                    >
                        <RefreshCw size={12} className={isFetching ? 'animate-spin' : ''} />
                        {isFetching ? 'Buscando...' : 'Atualizar'}
                    </button>
                </div>

                {!(contrato.id || contrato.external_reference) ? (
                    <div className="p-8 text-center bg-surface-50 rounded-3xl border border-dashed border-surface-200">
                        <AlertCircle size={32} className="text-warning-400 mx-auto mb-2" />
                        <p className="text-sm text-surface-700/50 font-semibold">Contrato sem chave de referência válida. Não é possível buscar cobranças no Asaas.</p>
                    </div>
                ) : loadingCharges ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-3">
                        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
                        <p className="text-sm text-surface-700/50 animate-pulse">Buscando cobranças no Asaas...</p>
                    </div>
                ) : !asaasCharges || asaasCharges.length === 0 ? (
                    <div className="p-10 text-center bg-surface-50 rounded-[2.5rem] border border-dashed border-surface-200">
                        <FileText size={40} className="text-surface-700/10 mx-auto mb-3" />
                        <p className="text-sm text-surface-700/50 font-semibold italic">Nenhuma cobrança encontrada no Asaas para este contrato.</p>
                        <p className="text-xs text-surface-700/30 mt-1">Chave: {contrato.external_reference}</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {asaasCharges.map((charge: any) => (
                            <AsaasChargeItem key={charge.id} charge={charge} />
                        ))}
                    </div>
                )}
            </div>

            {/* Suporte */}
            <div className="p-8 bg-primary-900 rounded-[2.5rem] text-white relative overflow-hidden group">
                <div className="relative z-10">
                    <h4 className="text-xl font-black mb-1">Central de Atendimento</h4>
                    <p className="text-primary-100/70 font-medium mb-6">Precisa de ajuda com este contrato ou fatura?</p>
                    <button className="w-full py-4 bg-white text-primary-900 rounded-[1.25rem] font-black flex items-center justify-center gap-3 shadow-xl active:scale-95 transition-all">
                        <ExternalLink size={20} /> FALAR COM SUPORTE
                    </button>
                </div>
                <div className="absolute -right-10 -bottom-10 opacity-10 group-hover:scale-125 transition-transform duration-700 blur-sm">
                    <ShieldCheck size={200} />
                </div>
            </div>
        </div>
    )
}

// ── Componente de cada cobrança do Asaas ──────────────────────────────────────
function AsaasChargeItem({ charge }: { charge: any }) {
    const [expanded, setExpanded] = useState(false)
    const [copied, setCopied] = useState(false)

    const isPaid = ['RECEIVED', 'CONFIRMED', 'RECEIVED_IN_CASH'].includes(charge.status)
    const isOverdue = charge.status === 'OVERDUE'
    const isPending = charge.status === 'PENDING'

    const statusLabel = isPaid ? 'PAGO' : isOverdue ? 'ATRASADO' : 'PENDENTE'
    const statusColor = isPaid ? 'text-success-600' : isOverdue ? 'text-danger-600' : 'text-warning-600'
    const iconBg = isPaid ? 'bg-success-50' : isOverdue ? 'bg-danger-50' : 'bg-warning-50'

    const handleCopy = (e: React.MouseEvent) => {
        e.stopPropagation()
        navigator.clipboard.writeText(charge.pix_copia_cola || '')
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    return (
        <div className={`bg-white rounded-[2rem] border-2 transition-all overflow-hidden
            ${isOverdue ? 'border-danger-100 ring-2 ring-danger-50' : expanded ? 'border-primary-100 shadow-lg' : 'border-surface-100 shadow-sm'}`}>
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full p-6 flex items-center justify-between text-left gap-4"
            >
                <div className="flex items-center gap-4 min-w-0">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
                        {isPaid ? <CheckCircle2 size={24} className="text-success-600" /> :
                         isOverdue ? <AlertCircle size={24} className="text-danger-600" /> :
                         <Clock size={24} className="text-warning-600" />}
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-black text-surface-900 leading-tight mb-1 truncate">
                            {charge.description || (charge.status === 'PENDING' ? 'Cobrança Pendente' : 'Cobrança')}
                        </p>
                        <p className="text-xs text-surface-700/50 font-bold flex items-center gap-1.5">
                            <Calendar size={11} /> Vence {formatDate(charge.due_date)}
                            {isOverdue && <span className="bg-danger-600 text-white text-[9px] px-2 py-0.5 rounded-full font-black ml-1">ATRASADA</span>}
                        </p>
                    </div>
                </div>
                <div className="text-right flex items-center gap-3 flex-shrink-0">
                    <div>
                        <p className="text-base font-black text-surface-900">{formatCurrency(charge.value)}</p>
                        <p className={`text-[10px] font-black uppercase ${statusColor}`}>{statusLabel}</p>
                    </div>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-transform ${expanded ? 'rotate-180 bg-surface-100' : 'bg-surface-50'}`}>
                        <ChevronDown size={18} className="text-surface-400" />
                    </div>
                </div>
            </button>

            {expanded && (
                <div className="px-6 pb-8 pt-4 border-t border-surface-100/50 bg-gradient-to-b from-surface-50/50 to-white animate-fade-in space-y-4">
                    {!isPaid && (
                        <div className="flex flex-col gap-3">
                            {/* Botão principal: Link da fatura no Asaas */}
                            {charge.invoice_url ? (
                                <a
                                    href={charge.invoice_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="w-full h-16 flex items-center justify-center gap-3 bg-primary-600 text-white font-black text-sm rounded-2xl shadow-xl shadow-primary-600/20 active:scale-[0.98] transition-all"
                                >
                                    <ExternalLink size={20} /> PAGAR AGORA (ASAAS)
                                </a>
                            ) : (
                                <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100 flex gap-3 items-start">
                                    <Info size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                                    <p className="text-xs text-blue-700">Link de pagamento não disponível. Entre em contato com o suporte.</p>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-3">
                                {charge.pix_copia_cola && (
                                    <button onClick={handleCopy}
                                        className="h-14 flex items-center justify-center gap-2 bg-white border-2 border-surface-100 text-surface-700 font-black text-xs rounded-2xl">
                                        {copied ? <Check size={18} className="text-success-600" /> : <Copy size={18} />}
                                        {copied ? 'COPIADO!' : 'COPIAR PIX'}
                                    </button>
                                )}
                                {charge.bank_slip_url && (
                                    <a href={charge.bank_slip_url} target="_blank" rel="noreferrer"
                                        className="h-14 flex items-center justify-center gap-2 bg-white border-2 border-surface-100 text-surface-700 font-black text-xs rounded-2xl">
                                        <Receipt size={18} /> BOLETO PDF
                                    </a>
                                )}
                            </div>
                        </div>
                    )}

                    {isPaid && (
                        <div className="p-6 bg-success-50 rounded-2xl border-2 border-success-100 flex flex-col items-center text-center gap-2">
                            <div className="w-12 h-12 bg-success-600 text-white rounded-full flex items-center justify-center mb-1">
                                <Check size={24} />
                            </div>
                            <h5 className="font-black text-success-900 text-lg uppercase">Fatura Liquidada</h5>
                            {charge.payment_date && (
                                <p className="text-sm text-success-700/70 font-bold">
                                    Confirmado em {formatDate(charge.payment_date)}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Info extra */}
                    <div className="grid grid-cols-2 gap-3 text-xs">
                        <div className="p-3 bg-surface-50 rounded-xl">
                            <p className="text-surface-500 font-bold uppercase mb-0.5">Tipo</p>
                            <p className="font-black text-surface-800">{charge.billing_type}</p>
                        </div>
                        <div className="p-3 bg-surface-50 rounded-xl">
                            <p className="text-surface-500 font-bold uppercase mb-0.5">Empresa</p>
                            <p className="font-black text-surface-800">{charge.empresa}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

function BenefitItem({ text }: { text: string }) {
    return (
        <div className="flex items-center gap-3 p-4 bg-surface-50/50 border border-surface-100 rounded-2xl">
            <CheckCircle2 size={18} className="text-success-500" />
            <span className="text-sm font-bold text-surface-700">{text}</span>
        </div>
    )
}
